import { BugIcon } from '@phosphor-icons/react';
import { useEffect, useState, type JSX } from 'react';

import { ImageDropzone } from '@/components/issue-reporting/ImageDropzone';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCreateIssueReportMutation } from '@/hooks/useIssueReportQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import { collectClientContext, getConsoleErrors, getNetworkErrors } from '@/lib/issue-reporting/debugBuffer';
import { MAX_IMAGES, validateImageFile } from '@/lib/issue-reporting/imageValidation';
import { toast } from '@/lib/toast';
import {
  IssueCategory,
  IssueSeverity,
  type CreateIssueReportInput,
} from '@/types/issue-reports';

const SEVERITY_OPTIONS: readonly { value: IssueSeverity; label: string }[] = [
  { value: IssueSeverity.Low, label: 'Low' },
  { value: IssueSeverity.Medium, label: 'Medium' },
  { value: IssueSeverity.High, label: 'High' },
  { value: IssueSeverity.Critical, label: 'Critical' },
];

const CATEGORY_OPTIONS: readonly { value: IssueCategory; label: string }[] = [
  { value: IssueCategory.Bug, label: 'Bug' },
  { value: IssueCategory.UiVisual, label: 'UI / Visual' },
  { value: IssueCategory.Performance, label: 'Performance' },
  { value: IssueCategory.DataIncorrect, label: 'Incorrect data' },
  { value: IssueCategory.FeatureRequest, label: 'Feature request' },
  { value: IssueCategory.Other, label: 'Other' },
];

function isSeverity(v: string): v is IssueSeverity {
  return (Object.values(IssueSeverity) as string[]).includes(v);
}
function isCategory(v: string): v is IssueCategory {
  return (Object.values(IssueCategory) as string[]).includes(v);
}

/**
 * Global "Report an issue" widget — a fixed FAB that opens a dialog. Mounted once in the AppShell so
 * it appears on every authenticated screen. Renders nothing when logged out (the AppShell itself only
 * renders for authenticated users, but we guard anyway so the component is safe to mount anywhere).
 *
 * On submit it bundles the typed fields with auto-captured client context + the scrubbed
 * console/network diagnostic buffers and POSTs multipart to /api/issue-reports.
 */
export function ReportIssueWidget(): JSX.Element | null {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);

  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<IssueSeverity>(IssueSeverity.Medium);
  const [category, setCategory] = useState<IssueCategory>(IssueCategory.Bug);
  const [images, setImages] = useState<File[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useCreateIssueReportMutation();

  // Paste-to-attach: while the dialog is open, capture image blobs from clipboard paste anywhere.
  useEffect(() => {
    if (!open) return;
    const onPaste = (e: ClipboardEvent): void => {
      const items = e.clipboardData?.items;
      if (items === undefined) return;
      const pasted: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file !== null && validateImageFile(file) === null) {
            pasted.push(file);
          }
        }
      }
      if (pasted.length > 0) {
        setImages((prev) => prev.slice(0, MAX_IMAGES).concat(pasted).slice(0, MAX_IMAGES));
      }
    };
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('paste', onPaste);
    };
  }, [open]);

  function resetForm(): void {
    setDescription('');
    setSeverity(IssueSeverity.Medium);
    setCategory(IssueCategory.Bug);
    setImages([]);
    setFormError(null);
  }

  function handleOpenChange(next: boolean): void {
    if (mutation.isPending) return; // don't close mid-submit
    setOpen(next);
    if (!next) resetForm();
  }

  function handleSubmit(): void {
    setFormError(null);
    if (description.trim() === '') {
      setFormError('Please describe the issue.');
      return;
    }
    const input: CreateIssueReportInput = {
      description: description.trim(),
      severity,
      category,
      context: collectClientContext(),
      consoleErrors: getConsoleErrors(),
      networkErrors: getNetworkErrors(),
    };
    mutation.mutate(
      { input, images },
      {
        onSuccess: () => {
          toast.success('Thanks — your issue was reported.');
          setOpen(false);
          resetForm();
        },
        onError: (err) => {
          toast.error('Could not submit your report', { description: err.message });
        },
      },
    );
  }

  if (!isAuthenticated) return null;

  return (
    <>
      <Button
        type="button"
        onClick={() => { setOpen(true); }}
        aria-label="Report an issue"
        data-testid="report-issue-fab"
        className="fixed bottom-4 right-4 z-40 h-12 gap-2 rounded-full shadow-lg"
      >
        <BugIcon size={18} weight="fill" />
        <span className="hidden sm:inline">Report an issue</span>
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="report-issue-dialog">
          <DialogHeader>
            <DialogTitle>Report an issue</DialogTitle>
            <DialogDescription>
              Describe what went wrong. We automatically attach your current page, browser, and recent
              errors to help us debug.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="issue-description">
                Description <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="issue-description"
                value={description}
                onChange={(e) => { setDescription(e.target.value); }}
                placeholder="What happened? What did you expect?"
                rows={4}
                maxLength={4000}
                disabled={mutation.isPending}
                data-testid="issue-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="issue-severity">Severity</Label>
                <Select
                  value={severity}
                  onValueChange={(v) => {
                    if (isSeverity(v)) setSeverity(v);
                  }}
                  disabled={mutation.isPending}
                >
                  <SelectTrigger id="issue-severity" data-testid="issue-severity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="issue-category">Category</Label>
                <Select
                  value={category}
                  onValueChange={(v) => {
                    if (isCategory(v)) setCategory(v);
                  }}
                  disabled={mutation.isPending}
                >
                  <SelectTrigger id="issue-category" data-testid="issue-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Screenshots</Label>
              <ImageDropzone files={images} onChange={setImages} disabled={mutation.isPending} />
            </div>

            {formError !== null && (
              <p className="text-sm text-destructive" data-testid="issue-form-error">
                {formError}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { handleOpenChange(false); }}
              disabled={mutation.isPending}
              data-testid="issue-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={mutation.isPending}
              data-testid="issue-submit"
            >
              {mutation.isPending ? 'Submitting…' : 'Submit report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
